import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ENQUIRY_STATUSES, TREATMENT_STATUSES } from "@/lib/constants";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

const today = () => new Date().toISOString().slice(0, 10);

export default function PatientForm({ onClose, patient = null }) {
  const qc = useQueryClient();
  const isEdit = !!patient;
  const [form, setForm] = useState({
    patient_id: patient?.patient_id || "",
    enquiry_date: patient?.enquiry_date || today(),
    consultation_date: patient?.consultation_date || "",
    acquisition_source: patient?.acquisition_source || "",
    source_details: patient?.source_details || "",
    enquiry_status: patient?.enquiry_status || "New",
    treatment_status: patient?.treatment_status || "Not Started",
    sessions_purchased: patient?.sessions_purchased ?? "",
    revenue: patient?.revenue ?? "",
    notes: patient?.notes || "",
  });
  const [sources, setSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    http.get("/sources").then(({ data }) => setSources([...(data.default || []), ...(data.custom || [])]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.acquisition_source) { setErr("Please pick an acquisition source"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        consultation_date: form.consultation_date || null,
        sessions_purchased: form.sessions_purchased === "" ? null : Number(form.sessions_purchased),
        revenue: form.revenue === "" ? null : Number(form.revenue),
      };
      if (isEdit) {
        await http.put(`/patients/${patient._id}`, payload);
        toast.success("Patient updated");
      } else {
        await http.post("/patients", payload);
        toast.success("Patient added");
      }
      qc.invalidateQueries({ queryKey: ["patients"] });
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="patient-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit patient record" : "Add new patient"}</DialogTitle>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800 flex gap-2 items-start">
          <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Please avoid entering sensitive medical or clinical information into this tool. Use Patient ID or initials.</span>
        </div>

        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 pt-2">
          <F label="Patient ID / Initials">
            <Input required value={form.patient_id} onChange={(e) => upd("patient_id")(e.target.value)} placeholder="e.g. P-1024 or A.K." data-testid="pf-patient-id" />
          </F>
          <F label="Acquisition Source">
            <Select value={form.acquisition_source} onValueChange={upd("acquisition_source")}>
              <SelectTrigger data-testid="pf-source"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Date of Enquiry">
            <Input type="date" required value={form.enquiry_date} onChange={(e) => upd("enquiry_date")(e.target.value)} data-testid="pf-enq-date" />
          </F>
          <F label="Date of First Consultation">
            <Input type="date" value={form.consultation_date} onChange={(e) => upd("consultation_date")(e.target.value)} data-testid="pf-cons-date" />
          </F>
          <F label="Specific Source / Referrer (optional)" full>
            <Input value={form.source_details} onChange={(e) => upd("source_details")(e.target.value)} placeholder="e.g. Dr. Sharma, Mrs. Mehta, Google Maps" data-testid="pf-source-details" />
          </F>
          <F label="Enquiry Status">
            <Select value={form.enquiry_status} onValueChange={upd("enquiry_status")}>
              <SelectTrigger data-testid="pf-enq-status"><SelectValue /></SelectTrigger>
              <SelectContent>{ENQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Treatment Status">
            <Select value={form.treatment_status} onValueChange={upd("treatment_status")}>
              <SelectTrigger data-testid="pf-tx-status"><SelectValue /></SelectTrigger>
              <SelectContent>{TREATMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Sessions Purchased (optional)">
            <Input type="number" min="0" value={form.sessions_purchased} onChange={(e) => upd("sessions_purchased")(e.target.value)} data-testid="pf-sessions" />
          </F>
          <F label="Revenue (optional)">
            <Input type="number" min="0" step="0.01" value={form.revenue} onChange={(e) => upd("revenue")(e.target.value)} data-testid="pf-revenue" />
          </F>
          <F label="Notes (optional)" full>
            <Textarea rows={3} value={form.notes} onChange={(e) => upd("notes")(e.target.value)} data-testid="pf-notes" />
          </F>

          {err && <div className="sm:col-span-2 text-sm text-red-600" data-testid="pf-error">{err}</div>}

          <DialogFooter className="sm:col-span-2 gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="pf-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pf-submit">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Add patient"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
